<template>
    <dialog v-show="open" ref="dialogRef" class="privacy-dialog">
        <div class="text-center flex flex-col gap-3 items-center overflow-y-auto">
            <div><i class="bi bi-lock text-2xl" aria-hidden="true" /></div>
            <h2 class="w-full title-subsection text-valence-grey-850">{{ modalHeader }}</h2>
            <MarkdownText v-if="organizationDetails?.custom_privacy_modal_message" :text="organizationDetails.custom_privacy_modal_message" />
            <div v-else class="body text-valence-grey-750 w-96 max-w-full mx-auto">
                Read our full
                <a class="hover:text-[#4B508F] text-valence-primary-500 underline" href="https://www.valence.co/other/privacy" target="_blank">privacy policy</a>
                and
                <a class="hover:text-[#4B508F] text-valence-primary-500 underline" href="https://www.valence.co/other/ai-ethics-pledge" target="_blank">AI Ethics Pledge</a>
                to understand how we protect and handle your information.
            </div>
        </div>
        <div class="inline-block mx-auto shrink-0">
            <button type="button" class="button button-primary" @click="open = false">Dismiss</button>
        </div>
    </dialog>
</template>

<script setup lang="ts">
import type { OrganizationDetails } from "~vue/appTypes";
import MarkdownText from "~vue/MarkdownText.vue";
import { computed, inject, onMounted, onUnmounted, type Ref, useTemplateRef, watch } from "vue";

const open = defineModel<boolean>("open", { default: false });

const dialogRef = useTemplateRef<HTMLDialogElement>("dialogRef");
const organizationDetails = inject<Ref<OrganizationDetails | null>>("organization_details");
const modalHeader = computed(() => organizationDetails?.value?.privacy_modal_header || "Your privacy is important to us");

watch(open, (value) => {
    if (value) {
        dialogRef.value?.showModal();
    } else {
        dialogRef.value?.close();
    }
});

function handleDialogCancel(e: Event) {
    e.preventDefault();
    open.value = false;
}

onMounted(() => dialogRef.value?.addEventListener("cancel", handleDialogCancel));
onUnmounted(() => dialogRef.value?.removeEventListener("cancel", handleDialogCancel));
</script>

<style scoped>
@reference "../../../css/tailwind.css";
.privacy-dialog {
    @apply top-auto bottom-0 md:top-0 rounded-tl-3xl rounded-tr-3xl md:rounded-3xl bg-white px-3 py-5 md:px-6 md:py-10 mx-auto w-full max-w-full md:max-w-md max-h-[80vh] flex flex-col gap-10 items-center overflow-hidden;
    box-shadow: 0px 4px 24px rgba(0, 0, 0, 0.12);
}

dialog.privacy-dialog::backdrop {
    background: rgba(23, 55, 100, 0.4);
    backdrop-filter: blur(4px);
}
</style>
